import { Download, File } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// O worker do pdfjs sai de UM lugar só — o mesmo módulo que o visualizador dos
// autos importa. Aqui havia uma segunda cópia da mesma configuração, escrevendo
// no mesmo global: duas para manter em dia no dia em que a resolução do worker
// mudar, e nenhum sinal de que a outra existia.
import '@/lib/pdfWorker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MAX_PAGE_WIDTH = 750;

export interface PreviewableDocument {
  id: string;
  nome: string;
  mime: string;
}

interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: PreviewableDocument | null;
  previewUrl: string;
  onDownload: () => void;
  /** Optional slot rendered before the file name in the header */
  headerIcon?: React.ReactNode;
}

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  document: doc,
  previewUrl,
  onDownload,
  headerIcon,
}: DocumentPreviewDialogProps) {
  const [pdfNumPages, setPdfNumPages] = useState(0);
  // Largura da página = largura da área, com teto nos 750px de sempre. Era
  // `width={750}` fixo, que cortava a página no celular. O elemento fica em
  // estado (callback ref) porque a área só monta junto com o PDF.
  const [areaEl, setAreaEl] = useState<HTMLDivElement | null>(null);
  const [areaWidth, setAreaWidth] = useState(0);

  useEffect(() => {
    if (!areaEl) return;
    const medir = () => setAreaWidth(areaEl.clientWidth);
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(areaEl);
    return () => observer.disconnect();
  }, [areaEl]);

  // 16px de folga lateral.
  const larguraPagina = areaWidth > 0 ? Math.min(MAX_PAGE_WIDTH, areaWidth - 16) : MAX_PAGE_WIDTH;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setPdfNumPages(0);
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="inset-0 w-full h-full max-h-none translate-x-0 translate-y-0 max-w-none rounded-none p-0 gap-0 flex flex-col sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-w-4xl sm:max-h-[90vh] sm:h-[90vh] sm:w-full sm:rounded-lg sm:p-6 sm:gap-4"
      >
        <DialogHeader className="shrink-0 p-3 pr-12 border-b border-border sm:p-0 sm:border-b-0">
          <DialogTitle className="flex min-w-0 items-center gap-2">
            {headerIcon}
            <span className="min-w-0 [overflow-wrap:anywhere]">{doc?.nome}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Visualização do documento {doc?.nome}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-hidden">
          {doc && doc.mime === 'application/pdf' ? (
            <div ref={setAreaEl} className="h-full overflow-auto bg-muted/30">
              <Document
                file={previewUrl}
                onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
                loading={
                  <div className="flex items-center justify-center h-full min-h-[200px]">
                    <span className="text-sm text-muted-foreground">Carregando PDF...</span>
                  </div>
                }
                error={
                  <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-2">
                    <span className="text-sm text-muted-foreground">Erro ao carregar PDF</span>
                    <Button variant="outline" size="sm" onClick={onDownload}>
                      <Download className="mr-2 h-4 w-4" />
                      Baixar arquivo
                    </Button>
                  </div>
                }
              >
                {Array.from({ length: pdfNumPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={larguraPagina}
                    className="mx-auto mb-2"
                  />
                ))}
              </Document>
            </div>
          ) : doc && doc.mime.startsWith('image/') ? (
            <div className="flex items-center justify-center h-full overflow-auto p-3 sm:p-0">
              <img
                src={previewUrl}
                alt={doc.nome}
                className="max-w-full max-h-full rounded-md object-contain"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <File className="h-16 w-16 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                Visualização não disponível para este tipo de arquivo
              </p>
              <Button onClick={onDownload}>
                <Download className="mr-2 h-4 w-4" />
                Baixar arquivo
              </Button>
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 p-3 border-t border-border sm:p-0 sm:border-t-0">
          <Button variant="outline" onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
